// src/App.tsx
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { AuthProvider } from './state/AuthContext'
import { ThemeProvider } from './state/ThemeContext'
import { RequireAuth } from './components/RequireAuth'
import { ScrollToTop } from './components/ScrollToTop'
import { Shell } from './components/Shell'
import { LoginPage } from './pages/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { BillsListPage } from './pages/BillsListPage'
import { BillDetailRoute } from './pages/BillDetailPage'
import { EntitlementPage } from './pages/EntitlementPage'
import { ApprovalQueuePage } from './pages/ApprovalQueuePage'
import { RecordsPage } from './pages/RecordsPage'
// ★★ WAVE 1a — Annex A 2-4, the deposit surface over the real intake.
import { UploadPage } from './pages/UploadPage'
import { ContactsPage } from './pages/ContactsPage'
import { AuditPage } from './pages/AuditPage'
import { AuditArchivePage } from './pages/AuditArchivePage'
// ★★ WAVE 10 — referrals and paused bills: what needs an officer but is not a
// queue decision. See the page's own header for why both populations need a home.
import { AttentionPage } from './pages/AttentionPage'
import { WardClassesPage } from './pages/WardClassesPage'
import { WardRulesPage } from './pages/WardRulesPage'
import { AuditRetentionPage } from './pages/AuditRetentionPage'
import { ReprorationConsolePage } from './pages/ReprorationConsolePage'
import { SubsystemsPage } from './pages/SubsystemsPage'
import { IdentityProvidersPage } from './pages/IdentityProvidersPage'
import { AgencyMappingsPage } from './pages/AgencyMappingsPage'
import { MedicalSchemeCodesPage } from './pages/MedicalSchemeCodesPage'
import { HspGroupsPage } from './pages/HspGroupsPage'
import { HspGroupMembershipsPage } from './pages/HspGroupMembershipsPage'
import { MbimRecordsPage } from './pages/MbimRecordsPage'
import { ExclusionListPage } from './pages/ExclusionListPage'
import { EmailTemplatesPage } from './pages/EmailTemplatesPage'
import { AnnouncementsPage } from './pages/AnnouncementsPage'
import { SpecialAgencyCasesPage } from './pages/SpecialAgencyCasesPage'
import { ReferenceDataPage } from './pages/ReferenceDataPage'
import { SettlementBatchesPage } from './pages/SettlementBatchesPage'
import { CostEstimatePage } from './pages/CostEstimatePage'
// ANNEX A 22 - the MSO pro-ration rule engine: rules, editor, calculator, enquiry.
import { MsoRulesPage } from './pages/MsoRulesPage'
import { MsoRuleEditorPage } from './pages/MsoRuleEditorPage'
import { MsoCalculatorPage } from './pages/MsoCalculatorPage'
import { MsoCalculationsPage } from './pages/MsoCalculationsPage'
import { MsoComparisonPage } from './pages/MsoComparisonPage'
// WAVE 2b - Annex A story 13, the custom report builder's own screen.
import { CustomReportPage } from './pages/CustomReportPage'
// ADR 0011 - PSD account administration, read-only (Annex A 52-59).
import { AccountDirectoryPage } from './pages/AccountDirectoryPage'
import { UserAccountsPage } from './pages/UserAccountsPage'
import { AssumeUserPage } from './pages/AssumeUserPage'
import { SignedOutPage } from './pages/SignedOutPage'
import { AccessControlMatrixPage } from './pages/AccessControlMatrixPage'
import { HelpPage } from './pages/HelpPage'
import { CsCardPage } from './pages/CsCardPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { FaqPage } from './pages/FaqPage'
import { SupportTicketsPage } from './pages/SupportTicketsPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { NoticeChrome } from './components/NoticeChrome'
import {
  ContactNoticePage,
  PrivacyNoticePage,
  TermsNoticePage,
} from './pages/NoticePages'

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          {/* Every route change lands at the top of the new page — see the component. */}
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signed-out" element={<SignedOutPage />} />
            {/*
              The statutory notices, OUTSIDE `RequireAuth` deliberately.

              SGDS requires the Privacy and Terms links on every page, and a link that
              redirects the reader to a sign-in screen is not a link. The sign-in screen is
              also where a visitor is most likely to want the privacy statement, since it is
              where they are being asked to identify themselves.

              `/contact-us` and not `/contact`, because `/contacts` is already a data screen
              in this console — an agency contact directory — and two addresses one letter
              apart, one a legal notice and one a record surface, is a trap for whoever reads
              a URL out over a phone.
            */}
            <Route element={<NoticeChrome />}>
              <Route path="/privacy" element={<PrivacyNoticePage />} />
              <Route path="/terms" element={<TermsNoticePage />} />
              <Route path="/contact-us" element={<ContactNoticePage />} />
            </Route>
            <Route element={<RequireAuth />}>
              <Route element={<Shell />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/bills" element={<BillsListPage />} />
                {/*
                  `BillDetailRoute` exists solely to key the page on the
                  reference. React Router keeps the same element mounted across
                  a parameter change, so without it one bill's per-bill state —
                  a failed compute message, a half-typed rejection reason, an
                  open reject form — rendered under the next bill's reference.
                  See that component's own comment.
                */}
                <Route path="/bills/:reference" element={<BillDetailRoute />} />
                <Route path="/entitlement" element={<EntitlementPage />} />
                <Route path="/approvals" element={<ApprovalQueuePage />} />
                <Route path="/records" element={<RecordsPage />} />
                {/* ★★ WAVE 1a — Annex A 2-4, the upload surface over the real intake. */}
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/audit" element={<AuditPage />} />
                {/* ★★ WAVE 9 — P2C1 §12.134/§12.135, the four routes no screen reached. */}
                <Route path="/audit/archive" element={<AuditArchivePage />} />
                <Route path="/audit/retention" element={<AuditRetentionPage />} />
                {/* ★★★ WAVE 14 — P2C1 §12 RA row 7. The sweep and the alert file, which Wave 13 built and left unreachable. */}
                <Route path="/operations/reproration" element={<ReprorationConsolePage />} />
                <Route path="/attention" element={<AttentionPage />} />
                {/* ★ PHASE 6 (Tasks 18/19) — Annex A stories 26-28. */}
                <Route path="/administration/ward-classes" element={<WardClassesPage />} />
                <Route path="/administration/ward-rules" element={<WardRulesPage />} />
                {/* ANNEX A 22 - the MSO rule engine. PSD only; every route behind it answers 403 to anyone else. */}
                <Route path="/administration/mso-rules" element={<MsoRulesPage />} />
                <Route path="/administration/mso-rules/versions/:id" element={<MsoRuleEditorPage />} />
                <Route path="/mso/calculator" element={<MsoCalculatorPage />} />
                <Route path="/mso/calculations" element={<MsoCalculationsPage />} />
                <Route path="/mso/calculations/:id" element={<MsoCalculationsPage />} />
                <Route path="/mso/comparison" element={<MsoComparisonPage />} />
                {/* ★★★ WAVE 12 (P2) — Annex A story 14 criteria 1 and 2. */}
                <Route path="/administration/identity-providers" element={<IdentityProvidersPage />} />
                <Route path="/administration/subsystems" element={<SubsystemsPage />} />
                <Route path="/administration/agency-mappings" element={<AgencyMappingsPage />} />
                {/* ★ PHASE 6 (Task 20) — Annex A story 31. */}
                <Route path="/administration/medical-scheme-codes" element={<MedicalSchemeCodesPage />} />
                {/* ★ PHASE 6 (Task 21) — Annex A stories 29/30. */}
                <Route path="/administration/hsp-groups" element={<HspGroupsPage />} />
                <Route path="/administration/hsp-group-memberships" element={<HspGroupMembershipsPage />} />
                {/* ★ PHASE 6 (Task 22) — Annex A story 36. */}
                <Route path="/administration/mbim-records" element={<MbimRecordsPage />} />
                {/* ★ PHASE 6 (Task 23) — Annex A story 32. */}
                <Route path="/administration/exclusion-list" element={<ExclusionListPage />} />
                {/* ★ PHASE 6 (Task 24) — Annex A story 33. */}
                <Route path="/administration/email-templates" element={<EmailTemplatesPage />} />
                {/* ★ PHASE 6 (Task 25) — Annex A story 34. */}
                <Route path="/administration/announcements" element={<AnnouncementsPage />} />
                {/* ★ PHASE 6 (Task 26) — Annex A story 35. */}
                <Route path="/administration/special-agency-cases" element={<SpecialAgencyCasesPage />} />
                {/* ★ PHASE 10 (Task 35) — Annex A 40-47, read-only. */}
                <Route path="/reference-data" element={<ReferenceDataPage />} />
                {/* ★ PHASE 10 (Task 37) — Annex A 5/6/8, read-only. */}
                <Route path="/settlement-batches" element={<SettlementBatchesPage />} />
                {/* ★ PHASE 10 — Annex A story 51, financial counselling cost estimation. */}
                <Route path="/cost-estimate" element={<CostEstimatePage />} />
                {/* ★★ WAVE 2b — Annex A story 13, the custom report field picker. */}
                <Route path="/reports/custom" element={<CustomReportPage />} />
                {/* ★★ ADR 0011 — Annex A 52-59, read-only. The writes stay CAM's. */}
                <Route path="/administration/accounts" element={<AccountDirectoryPage />} />
                <Route path="/administration/users" element={<UserAccountsPage />} />
                <Route path="/assume" element={<AssumeUserPage />} />
                {/* ★★ Annex A story 17 — the access-control matrix's first screen. */}
                <Route path="/administration/access-control" element={<AccessControlMatrixPage />} />
                {/* ★ PHASE 10 — Annex A stories 24/25, online help and the user manual. */}
                <Route path="/help" element={<HelpPage />} />
                {/* ★ PHASE 10 — Annex A story 49, print CS card information. */}
                <Route path="/cs-card" element={<CsCardPage />} />
                {/* ★ PHASE 10 — Annex A story 50, the notification RECORD and its trigger. */}
                <Route path="/notifications" element={<NotificationsPage />} />
                {/* ★ PHASE 11 — Annex A story 39, the scoped FAQ. */}
                <Route path="/faq" element={<FaqPage />} />
                {/* ★ PHASE 11 — Annex A story 48, local support tickets. */}
                <Route path="/support-tickets" element={<SupportTicketsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  )
}
